import type { InputManager } from "../core/input";
import type { WeaponId } from "../content/schemas";
import { loadoutWeaponForSlot, shortWeaponName } from "../content/weapons";
import { EXTRACT_RADIUS_M } from "../simulation/constants";
import type { WorldSnapshot } from "../simulation/snapshot";

/** On-screen twin sticks + clustered actions for touch extract runs. */

const LAUNCH_SHOW_RADIUS_M = EXTRACT_RADIUS_M * 2.5;

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly arcBtn: HTMLButtonElement;
  private readonly torpBtn: HTMLButtonElement;
  private readonly launchBtn: HTMLButtonElement;
  private lastSecReady = true;
  private lastOrdReady = true;

  public constructor(
    host: HTMLElement,
    private readonly input: InputManager,
  ) {
    this.root = document.createElement("div");
    this.root.className = "touch-controls";
    this.root.innerHTML = `
      <div class="stick move" data-stick="move" aria-label="Move stick"><i class="stick-thumb"></i></div>
      <div class="stick aim" data-stick="aim" aria-label="Aim stick"><i class="stick-thumb"></i></div>
      <button type="button" data-btn="pause" class="touch-btn touch-pause" aria-label="Pause">II</button>
      <div class="touch-cluster touch-cluster-move">
        <button type="button" data-btn="boost" class="touch-btn touch-boost">Boost</button>
      </div>
      <div class="touch-cluster touch-cluster-aim">
        <button type="button" data-btn="fireSecondary" class="touch-btn touch-weapon" data-slot="secondary">
          <span class="touch-btn-label">Arc</span>
          <span class="touch-cd" aria-hidden="true"></span>
        </button>
        <button type="button" data-btn="fireOrdnance" class="touch-btn touch-weapon" data-slot="ordnance">
          <span class="touch-btn-label">Torp</span>
          <span class="touch-cd" aria-hidden="true"></span>
        </button>
        <button type="button" data-btn="interact" class="touch-btn touch-launch" hidden>Launch</button>
      </div>
    `;
    host.appendChild(this.root);
    this.arcBtn = this.root.querySelector('[data-btn="fireSecondary"]')!;
    this.torpBtn = this.root.querySelector('[data-btn="fireOrdnance"]')!;
    this.launchBtn = this.root.querySelector('[data-btn="interact"]')!;
    this.bind();
    this.setVisible(false);
  }

  public setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    this.root.classList.toggle("visible", visible);
  }

  /** Refresh labels, cooldown fill (mirrors weapon rail), and Launch proximity. */
  public sync(snap: WorldSnapshot): void {
    this.syncLoadout(snap.runInventory.weapons);
    this.syncCooldowns(snap);
    this.syncLaunch(snap);
  }

  /** One-shot pulse on Arc/Torp when first-minute Q/F coaching fires. */
  public pulseWeaponButtons(): void {
    if (this.root.hidden) return;
    for (const btn of [this.arcBtn, this.torpBtn]) {
      if (btn.disabled) continue;
      btn.classList.remove("coach-pulse");
      void btn.offsetWidth;
      btn.classList.add("coach-pulse");
      window.setTimeout(() => btn.classList.remove("coach-pulse"), 900);
    }
  }

  /** Refresh secondary/ordnance labels from current loadout. */
  public syncLoadout(weapons: readonly WeaponId[]): void {
    const secondary = loadoutWeaponForSlot(weapons, "secondary");
    const ordnance = loadoutWeaponForSlot(weapons, "ordnance");
    const secLabel = this.arcBtn.querySelector(".touch-btn-label");
    const ordLabel = this.torpBtn.querySelector(".touch-btn-label");
    if (secLabel) secLabel.textContent = shortWeaponName(secondary, "Arc");
    if (ordLabel) ordLabel.textContent = shortWeaponName(ordnance, "Torp");
    this.arcBtn.disabled = secondary == null;
    this.torpBtn.disabled = ordnance == null;
  }

  /** Compare prior ready state and report newly-ready secondary/ordnance. */
  public pollWeaponReady(snap: WorldSnapshot): { secondary: boolean; ordnance: boolean } {
    const sec = snap.weaponSlots.find((s) => s.slot === "secondary");
    const ord = snap.weaponSlots.find((s) => s.slot === "ordnance");
    const secReady = sec?.ready ?? true;
    const ordReady = ord?.ready ?? true;
    const secondary = Boolean(secReady && !this.lastSecReady && sec?.weaponId != null);
    const ordnance = Boolean(ordReady && !this.lastOrdReady && ord?.weaponId != null);
    this.lastSecReady = secReady;
    this.lastOrdReady = ordReady;
    return { secondary, ordnance };
  }

  public dispose(): void {
    this.root.remove();
  }

  private syncCooldowns(snap: WorldSnapshot): void {
    for (const slot of ["secondary", "ordnance"] as const) {
      const btn = slot === "secondary" ? this.arcBtn : this.torpBtn;
      const rail = snap.weaponSlots.find((s) => s.slot === slot);
      const fill = rail?.cooldownFill ?? 0;
      const ready = rail?.ready ?? false;
      btn.style.setProperty("--cd", `${Math.round(fill * 100)}%`);
      btn.classList.toggle("on-cooldown", fill > 0.02);
      btn.classList.toggle("weapon-ready", ready && rail?.weaponId != null);
    }
  }

  private syncLaunch(snap: WorldSnapshot): void {
    const docked = snap.phase === "carrier" || snap.phase === "extracted" || snap.phase === "dead";
    const dist = Math.hypot(snap.player.x - snap.carrierX, snap.player.y - snap.carrierY);
    const nearCarrier =
      dist <= LAUNCH_SHOW_RADIUS_M || snap.phase === "extracting" || snap.phase === "onboarding";
    const show = docked || nearCarrier;
    this.launchBtn.hidden = !show;
    this.launchBtn.textContent =
      snap.phase === "extracting" ? "Extract" : docked ? "Launch" : "Dock";
  }

  private bind(): void {
    for (const stick of this.root.querySelectorAll<HTMLElement>(".stick")) {
      const role = stick.dataset.stick === "aim" ? "aim" : "move";
      const start = (event: PointerEvent): void => {
        event.preventDefault();
        stick.setPointerCapture(event.pointerId);
        this.updateStick(stick, role, event);
      };
      stick.addEventListener("pointerdown", start);
      stick.addEventListener("pointermove", (event) => {
        if (!stick.hasPointerCapture(event.pointerId)) return;
        event.preventDefault();
        this.updateStick(stick, role, event);
      });
      const end = (event: PointerEvent) => {
        event.preventDefault();
        if (stick.hasPointerCapture(event.pointerId)) stick.releasePointerCapture(event.pointerId);
        const thumb = stick.querySelector<HTMLElement>(".stick-thumb");
        if (thumb) {
          thumb.style.setProperty("--tx", "0px");
          thumb.style.setProperty("--ty", "0px");
        }
        if (role === "move") this.input.setTouchMove(0, 0, false);
        else this.input.setTouchAim(0, 0, false);
      };
      stick.addEventListener("pointerup", end);
      stick.addEventListener("pointercancel", end);
    }

    for (const button of this.root.querySelectorAll<HTMLButtonElement>("button[data-btn]")) {
      const name = button.dataset.btn as
        "boost" | "fireSecondary" | "fireOrdnance" | "interact" | "pause";
      button.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        if (button.disabled || button.hidden) return;
        this.input.setTouchButton(name, true);
      });
      button.addEventListener("pointerup", () => this.input.setTouchButton(name, false));
      button.addEventListener("pointerleave", () => this.input.setTouchButton(name, false));
    }
  }

  private updateStick(el: HTMLElement, role: "move" | "aim", event: PointerEvent): void {
    const rect = el.getBoundingClientRect();
    const nx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width * 0.45);
    const ny = (event.clientY - (rect.top + rect.height / 2)) / (rect.height * 0.45);
    const thumb = el.querySelector<HTMLElement>(".stick-thumb");
    if (thumb) {
      const radius = rect.width * 0.32;
      thumb.style.setProperty("--tx", `${Math.max(-1, Math.min(1, nx)) * radius}px`);
      thumb.style.setProperty("--ty", `${Math.max(-1, Math.min(1, ny)) * radius}px`);
    }
    if (role === "move") this.input.setTouchMove(nx, ny, true);
    else this.input.setTouchAim(nx, ny, true);
  }
}
